import React from 'react';
import { X, Shield, FileText, Info, Mail } from 'lucide-react';

interface LegalModalProps {
  type: 'privacy' | 'terms' | 'about' | null;
  onClose: () => void;
}

const LegalModal: React.FC<LegalModalProps> = ({ type, onClose }) => {
  if (!type) return null;

  const content = {
    privacy: {
      title: 'Privacy Policy',
      icon: <Shield className="w-5 h-5 text-emerald-400" />,
      text: (
        <div className="space-y-4 text-slate-300 text-sm leading-relaxed">
          <p><strong>Effective Date:</strong> October 2023</p>
          <p>At <strong>SiteFinder.app</strong>, developed by <strong>Diffusio Studios</strong>, we prioritize your privacy. We do not sell your personal data.</p>
          
          <h4 className="text-white font-medium mt-4">1. Data Collection</h4>
          <p>We collect search queries to improve our AI results. Your search history is stored locally on your device via LocalStorage. We do not store a permanent profile of your activity on our servers.</p>

          <h4 className="text-white font-medium mt-4">2. AI & Third Parties</h4>
          <p>We use Google's Gemini API to process your natural language queries. Data sent to the AI is anonymized where possible. We are not responsible for how third-party AI providers process data, though we configure our API calls for minimal retention.</p>

          <h4 className="text-white font-medium mt-4">3. Contact</h4>
          <p>For privacy concerns, please contact us at: <br/><span className="text-primary-400">bartosz.szczepaniak66@gmail.com</span></p>
        </div>
      )
    },
    terms: {
      title: 'Terms of Service',
      icon: <FileText className="w-5 h-5 text-primary-400" />,
      text: (
        <div className="space-y-4 text-slate-300 text-sm leading-relaxed">
          <h4 className="text-white font-medium mt-4">1. Acceptance</h4>
          <p>By using SiteFinder.app ("the Service"), you agree to these terms. The Service is provided by <strong>Diffusio Studios</strong>.</p>

          <h4 className="text-white font-medium mt-4">2. AI Disclaimer</h4>
          <p>Results are generated by Artificial Intelligence. While we strive for accuracy, the AI may occasionally hallucinate or provide outdated links. Always verify tool pricing and terms on the official website of the tool found.</p>

          <h4 className="text-white font-medium mt-4">3. Liability</h4>
          <p>Diffusio Studios is not liable for any damages resulting from the use of tools recommended by our search engine. We act solely as a search conduit.</p>
        </div>
      )
    },
    about: {
      title: 'About Diffusio Studios',
      icon: <Info className="w-5 h-5 text-accent-cyan" />,
      text: (
        <div className="space-y-4 text-slate-300 text-sm leading-relaxed">
          <p><strong>SiteFinder.app</strong> is a product of <strong>Diffusio Studios</strong>.</p>
          <p>We specialize in building intelligent, "invisible" interfaces that solve complex problems with simple interactions. We believe the future of software is not in directories, but in direct answers.</p>
          
          <div className="bg-white/5 p-4 rounded-lg border border-white/5 mt-4">
            <h4 className="text-white font-medium mb-2 flex items-center gap-2">
              <Mail className="w-4 h-4" /> Contact Support
            </h4>
            <p className="text-xs text-slate-400">Have a suggestion or found a bug?</p>
            <a href="mailto:bartosz.szczepaniak66@gmail.com" className="text-primary-400 hover:text-white transition-colors mt-1 block">
              bartosz.szczepaniak66@gmail.com
            </a>
          </div>
        </div>
      )
    }
  };

  const activeContent = content[type];

  return (
    <div className="fixed inset-0 z-[60] flex items-center justify-center p-4 bg-black/60 backdrop-blur-md animate-fade-in font-sans">
      <div className="bg-surface-900 border border-white/10 rounded-2xl w-full max-w-lg shadow-2xl relative overflow-hidden flex flex-col max-h-[80vh]">
        
        {/* Header */}
        <div className="p-6 border-b border-white/5 flex justify-between items-center bg-surface-900/50 backdrop-blur-md">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-white/5 rounded-lg border border-white/5">
              {activeContent.icon}
            </div>
            <h2 className="text-xl font-semibold text-white tracking-tight">{activeContent.title}</h2>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/5 rounded-full transition-colors text-slate-400 hover:text-white">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-6 overflow-y-auto custom-scrollbar">
          {activeContent.text}
        </div>

        {/* Footer */}
        <div className="p-4 border-t border-white/5 bg-background/50 text-center">
          <p className="text-[10px] text-slate-500 uppercase tracking-widest">Diffusio Studios © 2024</p>
        </div>
      </div>
    </div>
  );
};

export default LegalModal;